import type { PluginListenerHandle } from "@capacitor/core";
import { registerPlugin, WebPlugin } from "@capacitor/core";

/**
 * Native microphone capture for mobile voice (`CohesiveAudioCapture`).
 *
 * The Swift side (packages/user-mobile/ios/App/App/CohesiveAudioCapturePlugin.swift)
 * owns AVAudioSession + AVAudioEngine with the `audio` background mode, so
 * capture survives screen lock and app switching. It streams ~100ms chunks of
 * 24kHz mono PCM16LE (base64 over the bridge) to JS; the JS layer
 * (VoiceSessionController) owns the transcription WebSocket, buffering,
 * chaining, and persistence. On web / simulator the plugin reports
 * unavailable and callers fall back to Web Audio capture (MicCapture).
 */

export interface AudioChunkEvent {
  /** base64 PCM16LE mono @ 24kHz, ~100ms (4800 bytes decoded). */
  data: string;
  /** Milliseconds since capture start on the native clock. Monotonic across
   * mute (muted chunks are dropped but the clock keeps advancing, so a pause
   * is a truthful gap in the capture timeline). */
  timestampMs: number;
  /** Increments each time the native engine restarts its tap (interruption
   * recovery). Chunk timestamps stay on the same monotonic clock. */
  epoch: number;
}

export interface CaptureStateEvent {
  state: "running" | "interrupted" | "stopped";
  /** e.g. "phone_call", "route_change", "media_services_reset" */
  reason?: string;
  /** AVAudioSession says resumption is appropriate after an interruption
   * ended. The plugin already attempted the engine restart when this fires. */
  shouldResume?: boolean;
}

export interface CohesiveAudioCapturePlugin {
  /** True only where the native capture implementation is present. */
  isAvailable(): Promise<{ available: boolean }>;
  start(): Promise<void>;
  stop(): Promise<void>;
  /** Rebuild the engine after an interruption (see AudioCaptureEngine). */
  resume(): Promise<void>;
  setMuted(options: { muted: boolean }): Promise<void>;
  addListener(
    eventName: "audioChunk",
    listener: (event: AudioChunkEvent) => void,
  ): Promise<PluginListenerHandle>;
  addListener(
    eventName: "captureState",
    listener: (event: CaptureStateEvent) => void,
  ): Promise<PluginListenerHandle>;
}

/** Web / not-yet-implemented stub — callers fall back to MicCapture. */
class CohesiveAudioCaptureWeb
  extends WebPlugin
  implements CohesiveAudioCapturePlugin
{
  async isAvailable(): Promise<{ available: boolean }> {
    return { available: false };
  }
  async start(): Promise<void> {
    throw this.unimplemented("Native audio capture is not available.");
  }
  async stop(): Promise<void> {
    throw this.unimplemented("Native audio capture is not available.");
  }
  async resume(): Promise<void> {
    throw this.unimplemented("Native audio capture is not available.");
  }
  async setMuted(): Promise<void> {
    throw this.unimplemented("Native audio capture is not available.");
  }
}

export const CohesiveAudioCapture = registerPlugin<CohesiveAudioCapturePlugin>(
  "CohesiveAudioCapture",
  { web: () => new CohesiveAudioCaptureWeb() },
);

/**
 * Resolves false on the web and on native shells that predate the plugin
 * (calling an unregistered native plugin throws — caught here).
 */
export async function isNativeAudioCaptureAvailable(): Promise<boolean> {
  try {
    const { available } = await CohesiveAudioCapture.isAvailable();
    return available;
  } catch {
    return false;
  }
}

/** Decode one bridge chunk into the ArrayBuffer the transcription WS wants. */
export function decodeAudioChunk(base64: string): ArrayBuffer {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
}
